
"use client";

import { motion, AnimatePresence } from "framer-motion";
import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import LazyImage from "@/components/LazyImage";

// Import icons from Lucide-react instead of Tabler icons
import { ArrowLeft, ArrowRight } from "lucide-react";

type Item = {
  quote?: string;
  name: string;  // This field is required and must be present in Industry objects
  designation?: string;
  src?: string;
  description?: string;
  examples?: string;
  icon?: React.ReactNode;
  id?: string;
  category?: string;
  title?: string;  // Added to accommodate the Industry type
  backgroundImageSrc?: string;  // Added to support background images
  imageAlt?: string;  // Added for accessibility
};

export const AnimatedTestimonials = ({
  items,
  autoplay = false,
  className,
  isIndustryDisplay = false,
  title,
}: {
  items: Item[];
  autoplay?: boolean;
  className?: string;
  isIndustryDisplay?: boolean;
  title?: React.ReactNode;
}) => {
  const [active, setActive] = useState(0);

  const handleNext = () => {
    setActive((prev) => (prev + 1) % items.length);
  };

  const handlePrev = () => {
    setActive((prev) => (prev - 1 + items.length) % items.length);
  };

  const isActive = (index: number) => {
    return index === active;
  };

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 5000);
      return () => clearInterval(interval);
    }
  }, [autoplay]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const randomRotateY = () => {
    return Math.floor(Math.random() * 21) - 10;
  };

  return (
    <div className={cn("max-w-sm md:max-w-4xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-20", className)}>
      {title && (
        <div className="text-center mb-12">
          {title}
        </div>
      )}
      
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
        <div>
          <div className="relative h-64 sm:h-72 md:h-80 w-full">
            <AnimatePresence>
              {items.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    scale: 0.9,
                    z: -100,
                    rotate: randomRotateY(),
                  }}
                  animate={{
                    opacity: isActive(index) ? 1 : 0.7,
                    scale: isActive(index) ? 1 : 0.95,
                    z: isActive(index) ? 0 : -100,
                    rotate: isActive(index) ? 0 : randomRotateY(),
                    zIndex: isActive(index)
                      ? 999
                      : items.length + 2 - index,
                    y: isActive(index) ? [0, -40, 0] : 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.9,
                    z: 100,
                    rotate: randomRotateY(),
                  }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 origin-bottom"
                >
                  {isIndustryDisplay ? (
                    <div className="h-full w-full rounded-3xl overflow-hidden relative">
                      {item.backgroundImageSrc ? (
                        <>
                          <LazyImage
                            src={item.backgroundImageSrc}
                            alt={item.imageAlt || `Background image representing ${item.name}`}
                            className="h-full w-full rounded-3xl"
                            aspectRatio="aspect-[4/3]"
                          />
                          <div className="absolute inset-0 bg-luxury-900/60 flex items-center justify-center">
                            <div className="text-gold w-32 h-32 flex items-center justify-center">
                              {item.icon}
                            </div>
                          </div>
                        </>
                      ) : (
                        // Fallback to gradient if no image provided
                        <div className="h-full w-full rounded-3xl bg-gradient-to-br from-gold/5 to-gold/20 flex items-center justify-center border border-gold/20 shadow-xl">
                          <div className="text-gold w-32 h-32 flex items-center justify-center">
                            {item.icon}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <img
                      src={item.src}
                      alt={item.name}
                      width={500}
                      height={500}
                      draggable={false}
                      className="h-full w-full rounded-3xl object-cover object-center"
                    />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
        <div className="flex justify-between flex-col py-4">
          <motion.div
            key={active}
            initial={{
              y: 20,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{
              y: -20,
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
          >
            {isIndustryDisplay ? (
              <>
                <div className="mb-3">
                  <span className="text-gold mr-2">{items[active].id}</span>
                  <h3 className="text-2xl font-bold text-luxury-900 inline">
                    {items[active].name}
                  </h3>
                </div>
                <p className="text-luxury-600 mb-6">
                  {items[active].description}
                </p>
                {items[active].examples && (
                  <div className="mt-4 pt-4 border-t border-luxury-100">
                    <div className="text-sm font-medium text-luxury-500">
                      <span className="text-gold font-bold">Example Tools:</span> {items[active].examples}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-luxury-900">
                  {items[active].name}
                </h3>
                <p className="text-sm text-luxury-600">
                  {items[active].designation}
                </p>
                <motion.p className="text-lg text-luxury-600 mt-8">
                  {items[active].quote?.split(" ").map((word, index) => (
                    <motion.span
                      key={index}
                      initial={{
                        filter: "blur(10px)",
                        opacity: 0,
                        y: 5,
                      }}
                      animate={{
                        filter: "blur(0px)",
                        opacity: 1,
                        y: 0,
                      }}
                      transition={{
                        duration: 0.2,
                        ease: "easeInOut",
                        delay: 0.02 * index,
                      }}
                      className="inline-block"
                    >
                      {word}&nbsp;
                    </motion.span>
                  ))}
                </motion.p>
              </>
            )}
          </motion.div>
          <div className="flex items-center gap-4 pt-8 md:pt-0">
            <button
              onClick={handlePrev}
              className="h-10 w-10 rounded-full bg-luxury-200 flex items-center justify-center group/button hover:bg-gold/20 transition-colors"
              aria-label="Previous"
            >
              <ArrowLeft className="h-5 w-5 text-luxury-900 group-hover/button:rotate-12 transition-transform duration-300" />
            </button>
            
            <div className="flex-1 text-center text-sm text-luxury-500">
              {active + 1} / {items.length}
            </div>
            
            <button
              onClick={handleNext}
              className="h-10 w-10 rounded-full bg-luxury-200 flex items-center justify-center group/button hover:bg-gold/20 transition-colors"
              aria-label="Next"
            >
              <ArrowRight className="h-5 w-5 text-luxury-900 group-hover/button:-rotate-12 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedTestimonials;
